import { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Edit2, X, Check } from 'lucide-react';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import toast from '@/hooks/useToast';

interface FamilyMember {
  id: string;
  name: string;
  relation: string;
  dateOfBirth?: string;
  bloodGroup?: string;
  allergies?: string;
  notes?: string;
}

const RELATIONS = ['SELF', 'SPOUSE', 'PARENT', 'CHILD', 'SIBLING', 'OTHER'];
const RELATION_EMOJI: Record<string, string> = {
  SELF: '🧑', SPOUSE: '💑', PARENT: '👴', CHILD: '👶', SIBLING: '👫', OTHER: '👤',
};
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

const emptyForm = { name: '', relation: 'OTHER', dateOfBirth: '', bloodGroup: '', allergies: '', notes: '' };

export const FamilyCabinetPage = () => {
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetch = () => {
    setLoading(true);
    api.get('/family-members').then(r => setMembers(r.data.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, []);

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
  const openEdit = (m: FamilyMember) => {
    setForm({ name: m.name, relation: m.relation, dateOfBirth: m.dateOfBirth?.split('T')[0] || '', bloodGroup: m.bloodGroup || '', allergies: m.allergies || '', notes: m.notes || '' });
    setEditingId(m.id);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        const res = await api.put(`/family-members/${editingId}`, form);
        setMembers(p => p.map(m => m.id === editingId ? res.data.data : m));
        toast.success('Member updated');
      } else {
        const res = await api.post('/family-members', form);
        setMembers(p => [...p, res.data.data]);
        toast.success('Family member added');
      }
      setShowForm(false);
    } catch { toast.error('Could not save member'); }
    finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    try {
      await api.delete(`/family-members/${id}`);
      setMembers(p => p.filter(m => m.id !== id));
      toast.info('Member removed');
    } catch { toast.error('Could not remove member'); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Users className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-2xl text-gray-800">Family Medicine Cabinet</h1>
            <p className="text-sm text-gray-500">Manage health profiles for your entire family</p>
          </div>
        </div>
        <Button size="sm" onClick={openAdd}><Plus className="h-4 w-4 mr-1.5" /> Add Member</Button>
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="bg-white rounded-card shadow-card p-6 mb-6 border-2 border-primary-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">{editingId ? 'Edit Member' : 'Add Family Member'}</h3>
            <button onClick={() => setShowForm(false)}><X className="h-4 w-4 text-gray-400" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Input label="Full Name *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Rahul Patel" />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Relation</label>
              <select value={form.relation} onChange={e => setForm(p => ({ ...p, relation: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                {RELATIONS.map(r => <option key={r} value={r}>{RELATION_EMOJI[r]} {r.charAt(0) + r.slice(1).toLowerCase()}</option>)}
              </select>
            </div>
            <Input label="Date of Birth" type="date" value={form.dateOfBirth} onChange={e => setForm(p => ({ ...p, dateOfBirth: e.target.value }))} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Blood Group</label>
              <select value={form.bloodGroup} onChange={e => setForm(p => ({ ...p, bloodGroup: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                <option value="">Select</option>
                {BLOOD_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <div className="mb-3">
            <Input label="Known Allergies" value={form.allergies} onChange={e => setForm(p => ({ ...p, allergies: e.target.value }))} placeholder="e.g. Penicillin, Sulfa" />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Medical Notes</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Chronic conditions, current medications..." rows={2} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none" />
          </div>
          <div className="flex gap-2">
            <Button loading={saving} onClick={save}><Check className="h-4 w-4 mr-1.5" /> Save</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Members grid */}
      {loading ? (
        <div className="space-y-3">{Array(2).fill(0).map((_, i) => <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />)}</div>
      ) : members.length === 0 ? (
        <div className="text-center py-16">
          <Users className="h-16 w-16 text-gray-200 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-600 mb-2">No family members added yet</h3>
          <p className="text-gray-400 text-sm mb-4">Add your family members to track their health profiles and medications.</p>
          <Button onClick={openAdd}><Plus className="h-4 w-4 mr-2" /> Add First Member</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {members.map(m => (
            <div key={m.id} className="bg-white rounded-card shadow-card p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{RELATION_EMOJI[m.relation] || '👤'}</span>
                  <div>
                    <p className="font-semibold text-gray-800">{m.name}</p>
                    <p className="text-xs text-gray-400 capitalize">{m.relation.toLowerCase()}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(m)} className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"><Edit2 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(m.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="space-y-1 text-xs text-gray-500">
                {m.bloodGroup && <p>🩸 Blood Group: <span className="font-semibold text-gray-700">{m.bloodGroup}</span></p>}
                {m.dateOfBirth && <p>🎂 DOB: {new Date(m.dateOfBirth).toLocaleDateString('en-IN')}</p>}
                {m.allergies && <p>⚠️ Allergies: {m.allergies}</p>}
                {m.notes && <p className="text-gray-400 italic">{m.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
